import React, {
    useCallback,
    useState, useEffect, useContext
} from 'react';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { ModalNextProvider } from '@jutro/components';
// eslint-disable-next-line import/no-extraneous-dependencies
import { GatewayDocumentService } from 'gw-capability-gateway-document';
// eslint-disable-next-line import/no-extraneous-dependencies
import { GatewayEndorsementService } from 'gw-capability-gateway-policychange';
import { useValidation } from 'gw-portals-validation-react';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { useAuthentication } from 'gw-digital-auth-react';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import { messages } from 'gw-capability-policychange-common-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import styles from './QuotePage.module.scss';
import metadata from './QuotePage.metadata.json5';
import NonBlockingMsgPopup from './NonBlockingMsgPopup';

function QuotePage(props) {
    const { wizardData, updateWizardData, underwritingIssues } = props;
    const filterUWIssuesInCustomOffering = true;
    const { submissionVM } = wizardData;
    const { authHeader, isLoggedIn } = useAuthentication();
    const [allValuable, setAllValuable] = useState();
    const [allMortgagee, setAllMortgagee] = useState();
    const translator = useContext(TranslatorContext);
    const { onValidate, isComponentValid } = useValidation('QuotePage');

    const BLOCKS_QUOTES = ['BlocksQuote', 'BlocksQuoteRelease'];
    const BLOCKS_BIND = 'BlocksBind';
    const NON_BLOCKING = 'NonBlocking';
    const [isLoading, setLoading] = useState(false);
    const DocumentPageService = {
        downloadDocument: GatewayDocumentService.downloadDocument
    };

    const getDisplayName = (propertyInfos, type) => {
        const availableTypeLists = propertyInfos
            .flat()
            .filter((val) => val.availableTypeListValues)
            .find((availableType) => availableType.id === 'ArticleType');
        const availableTypeListValues = availableTypeLists.availableTypeListValues || [];
        return availableTypeListValues.find((list) => list.code === type).displayKey;
    };

    useEffect(() => {
        const setValuableNames = () => {
            const valuable = _.get(
                submissionVM.value,
                'lobData.homeowners.offerings[0].coverages.schedules',
                []
            );
            const scheduleItems = valuable.map((valuables) => valuables.scheduleItems);
            const propertyInfos = valuable.map((valuables) => valuables.propertyInfos);
            const displayNames = _.flatMap(scheduleItems).map((item) => {
                return !_.isUndefined(item.itemData.ArticleType)
                    ? getDisplayName(propertyInfos, item.itemData.ArticleType.typeCodeValue)
                    : _.get(item, 'itemData.SchedItemDescriptionId.stringValue', '-');
            });
            setAllValuable(displayNames.join(', '));
        };

        const setMortgageeNames = () => {
            const mortgagee = _.get(
                submissionVM.value,
                'lobData.homeowners.coverables.additionalInterests',
                []
            );
            const mortgageeNames = mortgagee.map(
                (mortgagees) => mortgagees.policyAdditionalInterest
            );
            const displayNames = mortgageeNames.map((mortgageeName) => mortgageeName.displayName);
            setAllMortgagee(displayNames.join(', '));
        };
        if (isLoggedIn) {
            setValuableNames();
            setMortgageeNames();
        }
    }, [isLoggedIn, submissionVM]);

    const showModal = async (nonBlockingMessage) => {
        const componentProps = {
            title: messages.nonBlockingMessageTitle,
            actionBtnLabel: messages.modalOk,
            nonBlockingMessage: nonBlockingMessage,
        };
        const results = await ModalNextProvider.showModal(
            <NonBlockingMsgPopup {...componentProps} />
        );
        return results;
    };

    useEffect(() => {
        const nonBlockingMessage = underwritingIssues.filter(
            (uwIssue) => uwIssue.currentBlockingPoint === NON_BLOCKING
            && uwIssue.hasApprovalOrRejection === false
        );
        const hasNonBlockingMessage = nonBlockingMessage.length > 0;

        if (hasNonBlockingMessage) {
            try {
                showModal(nonBlockingMessage);
            } catch (err) {
                console.error(err);
            }
        }
    }, []);

    const generateQuoteDocument = useCallback(
        () => {
            ModalNextProvider.showConfirm({
                title: messages.baGenerateQuoteDocument,
                message: messages.baGenerateQuoteDocumentConf,
                status: 'warning',
                icon: 'mi-error-outline'
            }).then((results) => {
                if (results === 'cancel') {
                    return _.noop();
                }
                setLoading(true);
                GatewayEndorsementService.generateQuoteDocumentPortal(
                    submissionVM.value, false, authHeader
                ).then((response) => {
                    setLoading(false);
                    if (response.publicID && response.sessionID) {
                        const downloadUrl = DocumentPageService.downloadDocument(
                            response.publicID,
                            response.sessionID
                        );
                        window.open(downloadUrl, '_blank');
                    } else {
                        ModalNextProvider.showAlert({
                            title: 'Unable to generate quote document',
                            message: 'Problem occured while generating quote document. Please try again later',
                            status: 'error',
                            icon: 'mi-error-outline',
                            confirmButtonText: 'Ok'
                        }).catch(_.noop);
                    }
                }).catch((err) => {
                    console.log(err);
                    setLoading(false);
                });
            }, _.noop);
        },
        [submissionVM.value, authHeader, DocumentPageService]
    );

    const findBlockingPoint = (type) => {
        return underwritingIssues.some((offeringIssue) => {
            if (type === BLOCKS_BIND) {
                return offeringIssue.currentBlockingPoint === BLOCKS_BIND;
            }
            return _.includes(BLOCKS_QUOTES, offeringIssue.currentBlockingPoint);
        });
    };

    const hasQuoteOrIssueBlockingUWIssues = () => {
        return submissionVM.status.value === 'Draft' || underwritingIssues.some((offeringIssue) => {
            return _.includes(BLOCKS_QUOTES, offeringIssue.currentBlockingPoint)
            || _.includes(BLOCKS_BIND, offeringIssue.currentBlockingPoint);
        });
    };

    const changeNoteForUW = (value) => {
        _.set(submissionVM.value, 'baseData.noteForUW', value);
        updateWizardData(wizardData);
    };

    const handleNext = useCallback(async () => {
        if (submissionVM.baseData.noteForUW.value !== undefined) {
            await GatewayEndorsementService.createNoteForUW(
                submissionVM.value.jobID, submissionVM.baseData.noteForUW.value, authHeader
            );
        }
        return wizardData;
    }, [submissionVM, authHeader, wizardData]);

    const visibilityConditionForNoteToUW = () => {
        if (underwritingIssues.length === 0
                || (underwritingIssues.filter(
                    (uwIssue) => uwIssue.currentBlockingPoint === BLOCKS_BIND
                        || uwIssue.currentBlockingPoint === 'BlocksQuote'
                        || uwIssue.currentBlockingPoint === 'BlocksQuoteRelease'
                ).length === 0
                && ((underwritingIssues.filter(
                    (uwIssue) => uwIssue.currentBlockingPoint === NON_BLOCKING && uwIssue.hasApprovalOrRejection === false
                ).length > 0) || (underwritingIssues.filter(
                    (uwIssue) => uwIssue.currentBlockingPoint === NON_BLOCKING && uwIssue.hasApprovalOrRejection === true
                ).length >= 0)))) {
            return true;
        }
        return false;
    };

    const overrideProps = {
        valuables: {
            value: allValuable
        },
        mortgagee: {
            value: allMortgagee
        },
        underWritingIssues: {
            underwritingIssues: underwritingIssues,
            blockQuote: findBlockingPoint(),
            quoteID: submissionVM.jobID.value,
            filterUWIssuesInCustomOffering,
            job: submissionVM.baseData.jobType.value,
            futureRenewalAvailable: submissionVM.futureRenewalAvailable.value
        },
        hoCostAndPremiumGrid: {
            visible: !findBlockingPoint(),
        },
        policyEffectiveDate: {
            visible: findBlockingPoint(),
        },
        uwNoteForUnderwriter: {
            visible: visibilityConditionForNoteToUW(),
            value: submissionVM.baseData.noteForUW.value ? submissionVM.baseData.noteForUW.value : '',
            onValueChange: changeNoteForUW
        }
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, submissionVM, id, path, overrideProps);
        },
        [overrideProps, submissionVM]
    );

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            generateQuoteDocument: generateQuoteDocument
        }
    };

    return (
        <WizardPage
            onNext={handleNext}
            disableNext={!isComponentValid || hasQuoteOrIssueBlockingUWIssues()}
            cancelLabel={translator(appConfig.persona === 'policyholder' ? messages.cancelAllChanges : messages.cancel)}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                resolveValue={readValue}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
            />
        </WizardPage>
    );
}

QuotePage.propTypes = wizardProps;
export default QuotePage;
